import { CarbonRating, DietaryAttribute } from '../recipeV2/recipeV2Types';
import { RecipeDataCompleteness } from '../../../../mocks/data/demoRecipeTypes';

/** Types for the Range Planning "Menus" demo screen. Demo-only - no real backend exists for
 * menu authoring yet. menuId values 1/2/3 line up with src/mocks/data/recipeUsageData.ts so
 * Recipes > Usage links resolve to a real Menu detail here. */

export enum MenuStatus {
  Draft = 'Draft',
  Scheduled = 'Scheduled',
  Live = 'Live',
  Archived = 'Archived',
}

export enum MenuType {
  Breakfast = 'Breakfast',
  Lunch = 'Lunch',
  Dinner = 'Dinner',
  Hospitality = 'Hospitality',
  Events = 'Events',
  Retail = 'Retail',
  GrabAndGo = 'GrabAndGo',
  CoffeeBar = 'CoffeeBar',
  Care = 'Care',
  SchoolMeal = 'SchoolMeal',
  StaffDining = 'StaffDining',
  Special = 'Special',
}

export enum MenuCycle {
  SingleDay = 'SingleDay',
  Weekly = 'Weekly',
  TwoWeekCycle = 'TwoWeekCycle',
  ThreeWeekCycle = 'ThreeWeekCycle',
  Termly = 'Termly',
  Seasonal = 'Seasonal',
  EventBased = 'EventBased',
}

export interface MenuServicePeriod {
  name: string;
  startTime: string;
  endTime: string;
  days: string[];
}

/** Menu items store only the recipe relationship (id + price) - display name, category,
 * allergens etc. are resolved at render time by looking the id up against the RecipeV2
 * catalogue (full `recipes` first, falling back to lightweight `recipeSummaries`). This
 * keeps the menu structure faithful to the source data model instead of duplicating
 * recipe content inline on every dish. */
export interface MenuDish {
  recipeId: number;
  price: number;
}

export interface MenuCourse {
  name: string;
  dishes: MenuDish[];
}

/** A single day's course breakdown for menus that vary day-to-day (e.g. a school lunch
 * cycle: Monday > Main/Vegetarian/Dessert, Tuesday > Main/Vegetarian/Dessert). Optional -
 * menus that serve the same fixed set of dishes every service (hospitality, single-item
 * offers) just use the flat `courses` field and have no `dayStructure`. */
export interface MenuDayEntry {
  day: string;
  courses: MenuCourse[];
}

export interface MenuSite {
  siteName: string;
  region: string;
}

/** A sellable item offered directly on a menu without being built from a recipe (e.g. a
 * bottled drink, a bag of crisps, a side salad) - links through to the real Range Planning
 * Items catalogue (itemNumber) rather than duplicating item data here. */
export interface MenuStandaloneItem {
  itemNumber: string;
  description: string;
  price: number;
  category: string;
}

export interface MenuVersionEntry {
  version: number;
  date: string;
  changeSummary: string;
}

export interface MenuPublishing {
  publishStatus: MenuStatus;
  publishedDate?: string;
  channels: string[];
}

/** Trial programme details for menus running a live-site pilot ahead of full rollout -
 * sourced directly from the source dataset's `trial` block (siteCount/startDate/endDate/
 * reviewDate) where present. */
export interface MenuTrial {
  siteCount: number;
  startDate: string;
  endDate: string;
  reviewDate: string;
}

export interface Menu {
  id: number;
  name: string;
  description: string;
  status: MenuStatus;
  menuType: MenuType;
  menuCycle: MenuCycle;
  servicePeriods: MenuServicePeriod[];
  courses: MenuCourse[];
  dayStructure?: MenuDayEntry[];
  standaloneItems?: MenuStandaloneItem[];
  sites: MenuSite[];
  targetMarginPercent: number;
  versionHistory: MenuVersionEntry[];
  publishing: MenuPublishing;
  trial?: MenuTrial;
}

export interface MenuListResponse {
  data: Menu[];
  totalCount: number;
}

export interface CreateMenuRequest {
  name: string;
  description: string;
}

/** Aggregated allergen/dietary + carbon view derived from the recipes on a menu - computed
 * by the mocked client from RecipeV2 data, not stored on the Menu itself. */
export interface MenuRecipeSummary {
  recipeId: number;
  recipeName: string;
  carbonRating: CarbonRating;
  dietaryAttributes: DietaryAttribute[];
  costPerPortion: number;
  /** 'summary' when the recipe only exists as a lightweight catalogue entry (no full recipe
   * record yet) - the UI uses this to flag the row as incomplete/estimated. */
  dataCompleteness?: RecipeDataCompleteness;
}
